import { ChatMessage } from "@/app/api/chats/[chatId]/messages/messages.dto";
import FollowProfileButton from "@/components/common/follow-profile-button";
import ProfilePicture from "@/components/common/profile-picture";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useChat } from "@/providers/chat-provider";
import { hasIncognitoProfile } from "@/services/users";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ChevronRightIcon, VerifiedIcon } from "lucide-react";
import Link from "next/link";
import DeleteMessageButton from "./components/delete-message-button";
import MessageReactionButton from "./components/message-reaction-button";
import "./message.module.css";

type TMessageProps = {
	data: ChatMessage;
};

const Message = ({ data }: TMessageProps) => {
	const { user } = useAuth();
	const { activeChat, showChatWidget } = useChat();
	const queryClient = useQueryClient();
	const size = showChatWidget ? "small" : "normal";

	const { messageText, sender, updatedAt } = data;

	const isSender = sender?.id === user?.id;

	const messageClass = isSender ? "message message-sender" : "message message-recipient";

	// Get incognito profile data from cache
	const incognitoProfileData = queryClient.getQueryData<
		Awaited<ReturnType<typeof hasIncognitoProfile>>
	>(["hasIncognitoProfile"]);

	const isIncognito =
		(activeChat?.incognito ? activeChat?.incognito : activeChat?.rules?.isIncognitoChat) &&
		sender.incognito;

	return (
		<div
			className={cn(
				"group flex w-full items-center gap-2",
				isSender ? "flex-row-reverse" : "",
				size === "small" && "gap-1"
			)}
		>
			{isIncognito ? (
				<div>
					<ProfilePicture
						alternateText={
							isSender
								? incognitoProfileData?.username
									? incognitoProfileData?.username
									: sender.name
								: sender.name
						}
						profilePictureUrl={
							isSender
								? incognitoProfileData?.profilePicture
									? incognitoProfileData?.profilePicture
									: sender.image
								: sender.image
						}
						className={{
							container: "mx-auto h-10 w-10 rounded-full p-0.5",
							image: "rounded-full text-gray-600",
							badgeImage: "-bottom-3 h-6 w-6",
						}}
					/>
				</div>
			) : (
				<TooltipProvider>
					<Tooltip delayDuration={0}>
						<TooltipTrigger type="button">
							<ProfilePicture
								alternateText={sender.name}
								profilePictureUrl={sender.image}
								className={{
									container: "mx-auto h-10 w-10 rounded-full p-0.5",
									image: "rounded-full text-gray-600",
									badgeImage: "-bottom-3 h-6 w-6",
								}}
							/>
						</TooltipTrigger>

						<TooltipContent className="tooltip-content max-w-sm rounded-lg bg-white shadow">
							<div className="flex w-max items-center gap-4 px-4 py-2">
								<ProfilePicture
									profilePictureUrl={sender.image}
									className={{
										container:
											size === "small"
												? "h-7 w-7 rounded-full p-0.5"
												: "h-8 w-8 rounded-full p-0.5",
										badgeImage: size === "small" ? "-bottom-3 h-5 w-5" : "-bottom-4 h-6 w-6",
										image: "rounded-full text-gray-600",
									}}
								/>

								<div className="text-left">
									<div className="flex items-center gap-1">
										<h3 className="text-base font-semibold text-gray-800">{sender.name}</h3>

										{sender.isFounder ? (
											<VerifiedIcon className="h-4 w-4 text-brand-primary" />
										) : null}

										<div className="h-1 w-1 rounded-full bg-brand-primary" />

										{sender.username && (
											<FollowProfileButton
												username={sender.username}
												className="m-0 w-fit bg-transparent p-0 text-xs text-brand-primary md:p-0"
											/>
										)}
									</div>

									<span className="mt-1 block text-xs text-gray-600">
										{sender?.jobTitle
											? sender?.jobTitle
											: sender?.howDoYouIdentifyYourself
											? sender?.howDoYouIdentifyYourself
											: "-"}{" "}
										| {sender?.company || "-"}
									</span>

									<Link
										href={`/${sender.username}`}
										className="mt-3 flex items-center justify-start gap-1 text-brand-primary"
									>
										<span className="text-xs font-medium">Visit Profile</span>
										<ChevronRightIcon className="h-3 w-3" />
									</Link>
								</div>
							</div>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			)}

			<div className="flex w-full max-w-[75%] flex-col gap-px md:w-max">
				<div className="flex items-center justify-between gap-2">
					<p className="text-xxs font-medium capitalize text-gray-700">
						{isSender ? "You" : sender.name}
					</p>

					<span
						className={cn(
							"mt-px block text-[8px] text-gray-500",
							isSender ? "text-right" : "text-left"
						)}
					>
						{format(new Date(updatedAt), "HH:mm")}
					</span>
				</div>

				<div
					className={cn(
						"relative break-words rounded-lg p-2",
						isSender
							? "rounded-br-none bg-brand-primary text-white"
							: "rounded-bl-none bg-white text-gray-700",
						size === "small" ? "p-2 text-xs" : "min-w-[240px] text-sm",
						messageClass
					)}
				>
					<MessageReactionButton
						messageId={data.id}
						isReacted={data.isReacted}
						totalReactions={data.totalReactions}
						messageType={isSender ? "sent" : "received"}
					/>

					{(isSender || user?.isAdmin) && (
						<DeleteMessageButton messageId={data.id} chatId={activeChat?.uniqueChatId} />
					)}

					<div dangerouslySetInnerHTML={{ __html: messageText }} />
				</div>
			</div>
		</div>
	);
};

export default Message;
